import { useState, useEffect, useMemo, type ChangeEvent } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { styled } from "@mui/material/styles";
import axios from "../../plugins/axios";
import MyCKEditor from "../../ckeditor/MyCKEditor";
import { pageMargins } from "../../ckeditor/pageMargins";
import { setSection, getSection, selectSections } from "../../store/slices/configSlice";
import InsertPlaceholder from "./InsertPlaceholder";
import axios_raw from "axios";
import {
    Box,
    Button,
    Button as MuiIconButton,
    TextField,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    type SelectChangeEvent,
} from "@mui/material";

import { Save as SaveIcon } from "@mui/icons-material";

import { grey } from "@mui/material/colors";
// Vite-specific `?inline` query returns the processed CSS as a string. CRA used to
// give back a string by default; Vite serves CSS for side-effects only.
import ckcss from "../../ckeditor/ck-content.css?inline";

const IconButton = styled(MuiIconButton)`
  padding: 2px;
  margin-top: -2px;
  margin-left: 2px;
  min-width: 0;
  color: ${grey[700]};
`;

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

// configSlice's Section interface carries an index signature, so legacy fields
// like `html`, `question`, `isMandatory`, `dependsOnSectionId` stay typed as any.
type EditableSection = Record<string, any>;

interface SectionEditorProps {
    section: EditableSection;
    onSave: () => void;
}

const SectionEditor = ({ section, onSave }: SectionEditorProps) => {
    const dispatch = useAppDispatch();
    const sections = useAppSelector(selectSections);

    const [sectionInfo, setSectionInfo] = useState<EditableSection>(section);
    const [saving, setSaving] = useState(false);
    // CKEditor instance — typed loosely because MyCKEditor itself is still .js.
    const [editor, setEditor] = useState<any>();
    const [dependsOnFilter, setDependsOnFilter] = useState("");

    const styles = { targetDiv: { height: "calc(100vh - 240px)" } };

    const thisSection = useMemo(
        () => sections.find((s) => s.sectionId === section.sectionId),
        [section.sectionId, sections],
    );

    useEffect(() => {
        let html = "";
        if (thisSection) html = thisSection.html || "LOADING ...";
        if (editor) editor.setData(html);
    }, [editor, thisSection]);

    useEffect(() => {
        if (section.sectionId && !section.html) dispatch(getSection(section.sectionId));
    }, [dispatch, section, section.sectionId, section.html]);

    const filteredSections = useMemo(() => {
        const term = dependsOnFilter.trim().toLowerCase();
        if (!term) return sections;
        return sections.filter((s) => (s.key ?? "").toLowerCase().includes(term));
    }, [sections, dependsOnFilter]);

    const setSectionKey = (e: ChangeEvent<HTMLInputElement>) =>
        setSectionInfo({ ...sectionInfo, key: e.target.value });

    const setSectionQuestion = (e: ChangeEvent<HTMLInputElement>) =>
        setSectionInfo({ ...sectionInfo, question: e.target.value });

    const setSectionMandatory = (e: ChangeEvent<HTMLInputElement>) =>
        setSectionInfo({ ...sectionInfo, isMandatory: e.target.checked });

    const setDependsOn = (e: SelectChangeEvent<unknown>) =>
        setSectionInfo({ ...sectionInfo, dependsOnSectionId: e.target.value });

    const save = async () => {
        const html = editor.getData();

        if (
            html === sectionInfo.html &&
            sectionInfo.key === section.key &&
            sectionInfo.question === section.question &&
            sectionInfo.isMandatory === section.isMandatory &&
            sectionInfo.dependsOnSectionId === section.dependsOnSectionId
        ) {
            onSave();
            return;
        }

        const saveData = { ...sectionInfo, html };
        setSaving(true);
        const response = await axios.post(`${apiUrl}/savesection`, saveData);
        dispatch(setSection(response.data));
        setSaving(false);
        onSave();
    };

    const downloadWord = async () => {
        const converterOptions = {
            margin_right: pageMargins.right,
            margin_left: pageMargins.left,
            margin_top: pageMargins.top,
            margin_bottom: pageMargins.bottom,
            footer: [
                { html: "<span></span>", type: "first" },
                {
                    html: '<p><span class="pageNumber"></span></p>',
                    css: "p { font-size: 1em; color: hsl(0, 0%, 60%); text-align: center; margin-bottom:15px;}",
                },
            ],
        };

        const response = await axios_raw.post(
            "https://ckeditor-docx.greenwave-257981d3.westeurope.azurecontainerapps.io/v1/convert",
            {
                html: `<html><link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Raleway:900"><body><div class="ck-content">${editor.getData()}</div></body></html>`,
                css: ckcss,
                options: converterOptions,
            },
            { responseType: "blob" },
        );

        const blob = new Blob([response.data]);
        const link = window.document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute("target", "_blank");
        link.download = `${sectionInfo.key}.docx`;
        link.click();
    };

    const downloadPdf = async () => {
        const converterOptions = {
            margin_right: pageMargins.right,
            margin_left: pageMargins.left,
            margin_top: pageMargins.top,
            margin_bottom: pageMargins.bottom,
            footer_html: '<div class="styled-counter"><span class="pageNumber"></span></div>',
            header_and_footer_css: ckcss,
        };

        const response = await axios_raw.post(
            "https://ckeditor-pdf.greenwave-257981d3.westeurope.azurecontainerapps.io/v1/convert",
            {
                html: `<html><link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Raleway:900"><body><div class="ck-content">${editor.getData()}</div></body></html>`,
                css: ckcss,
                options: converterOptions,
            },
            { responseType: "blob" },
        );

        const blob = new Blob([response.data]);
        const link = window.document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute("target", "_blank");
        link.download = `${sectionInfo.key}.pdf`;
        link.click();
    };

    return (
        <Box display="flex" flexDirection="column" style={styles.targetDiv}>
            <Box display="flex" alignItems="center" gap="5px">
                <TextField
                    variant="outlined"
                    size="small"
                    label="Section Key"
                    inputProps={{ maxLength: 50 }}
                    value={sectionInfo.key || ""}
                    onChange={setSectionKey}
                    style={{ width: "300px" }}
                />
                <TextField
                    variant="outlined"
                    size="small"
                    label="Question"
                    inputProps={{ maxLength: 255 }}
                    value={sectionInfo.question || ""}
                    onChange={setSectionQuestion}
                    style={{ width: "300px" }}
                />
                <FormControl variant="outlined" margin="dense" style={{ margin: 0 }}>
                    <InputLabel shrink>Depends on section</InputLabel>
                    <Select
                        style={{ width: "250px" }}
                        displayEmpty
                        notched
                        value={sectionInfo.dependsOnSectionId || ""}
                        label="Depends on section"
                        fullWidth
                        onChange={setDependsOn}
                    >
                        <MenuItem>
                            <TextField
                                onKeyDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                fullWidth
                                value={dependsOnFilter}
                                onChange={(e) => setDependsOnFilter(e.target.value)}
                            />
                        </MenuItem>
                        <MenuItem value="">None</MenuItem>
                        {filteredSections.map((s) => (
                            <MenuItem key={s.sectionId} value={s.sectionId}>
                                {s.key} - {s.sectionId}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={<Switch checked={sectionInfo.isMandatory} onChange={setSectionMandatory} />}
                    label="Mandatory"
                />
                {editor && (
                    <IconButton
                        disabled={saving || (thisSection?.sectionId && !thisSection?.html)}
                        disableRipple={false}
                        size="large"
                    >
                        <SaveIcon onClick={save} />
                    </IconButton>
                )}
            </Box>
            <Box display="flex" alignItems="center">
                <InsertPlaceholder editor={editor} />
                <Button variant="contained" style={{ marginLeft: "10px" }} onClick={downloadWord}>
                    Download Word
                </Button>
                <Button variant="contained" style={{ marginLeft: "10px" }} onClick={downloadPdf}>
                    Download PDF
                </Button>
            </Box>
            <MyCKEditor type="section" setEditor={setEditor} editor={editor} onChange={() => {}} />
        </Box>
    );
};

export default SectionEditor;
